import React, { useCallback, useEffect, useState } from "react";
import { Grid, Box, Tooltip, IconButton, Typography, CircularProgress } from "@mui/material";
import { Container } from "@mui/material";
import Page from "../components/Page";
import AppWidgetSummary from "../sections/@dashboard/app/AppWidgetSummary";
import NewlyResitered from "../sections/@dashboard/app/NewlyResitered";
import DashboardCharts from "../sections/@dashboard/app/DashboardCharts";
import RecentPurchases from "../sections/@dashboard/app/RecentPurchases";
import Iconify from "../components/Iconify";

// ── Shimmer card that matches the exact shape of AppWidgetSummary ─────────────
const SHIMMER_COLORS = [
  { bg: "#f2ebff", shine: "rgba(255,255,255,0.6)" },
  { bg: "#e0f7fa", shine: "rgba(255,255,255,0.6)" },
  { bg: "#fff3e0", shine: "rgba(255,255,255,0.6)" },
  { bg: "#ffebee", shine: "rgba(255,255,255,0.6)" },
  { bg: "#fce4ec", shine: "rgba(255,255,255,0.6)" },
];

function ShimmerCard({ idx = 0 }) {
  const c = SHIMMER_COLORS[idx % SHIMMER_COLORS.length];
  return (
    <Box
      sx={{
        width: "100%",
        height: 200,
        borderRadius: 2.5,
        bgcolor: c.bg,
        overflow: "hidden",
        position: "relative",
        border: "1px solid rgba(145,158,171,0.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "60%",
          height: "100%",
          background: `linear-gradient(90deg, transparent, ${c.shine}, transparent)`,
          animation: "shimmer 1.6s infinite",
        },
        "@keyframes shimmer": {
          "0%":   { left: "-60%" },
          "100%": { left: "120%" },
        },
      }}
    >
      {/* Icon circle placeholder */}
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: "rgba(0,0,0,0.06)" }} />
      {/* Number placeholder */}
      <Box sx={{ width: 80, height: 28, borderRadius: 1, bgcolor: "rgba(0,0,0,0.07)" }} />
      {/* Label placeholder */}
      <Box sx={{ width: 100, height: 14, borderRadius: 1, bgcolor: "rgba(0,0,0,0.05)" }} />
    </Box>
  );
}

function ShimmerRect({ height = 350 }) {
  return (
    <Box
      sx={{
        width: "100%",
        height,
        borderRadius: 2.5,
        bgcolor: "#f5f5f5",
        overflow: "hidden",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "60%",
          height: "100%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
          animation: "shimmer 1.6s infinite",
        },
        "@keyframes shimmer": {
          "0%":   { left: "-60%" },
          "100%": { left: "120%" },
        },
      }}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────

export default function DashboardApp() {
  const [summary, setSummary] = useState(() => {
    try {
      const cached = localStorage.getItem("cache_dashboard_summary");
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });
  const [summaryLoading, setSummaryLoading] = useState(() => !summary || Object.keys(summary).length === 0);

  const [dashboard, setDashboard] = useState(() => {
    try {
      const cached = localStorage.getItem("cache_dashboard_charts");
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [chartsLoading, setChartsLoading] = useState(() => !dashboard);
  const [refreshing, setRefreshing]         = useState(false);

  const role      = localStorage.getItem("role");
  const teacherId = localStorage.getItem("id");
  const token     = localStorage.getItem("token");

  // ── Fast fetch: counts only (server-cached 5 min) ────────────────────────
  const fetchSummary = useCallback(async (forceRefresh = false) => {
    try {
      const url = forceRefresh
        ? `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-admin-dashboard-summary?t=${Date.now()}`
        : `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-admin-dashboard-summary`;
      const res  = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json?.data) {
        setSummary(json.data);
        localStorage.setItem("cache_dashboard_summary", JSON.stringify(json.data));
      }
    } catch (e) {
      console.error("Summary fetch error:", e);
    } finally {
      setSummaryLoading(false);
    }
  }, [token]);

  // ── Slow fetch: charts + tables ──────────────────────────────────────────
  const fetchCharts = useCallback(async (forceRefresh = false) => {
    try {
      const url = forceRefresh
        ? `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-admin-dashboard?t=${Date.now()}`
        : `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-admin-dashboard`;
      const res  = await fetch(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json?.data) {
        setDashboard(json.data);
        localStorage.setItem("cache_dashboard_charts", JSON.stringify(json.data));
      }
    } catch (e) {
      console.error("Charts fetch error:", e);
    } finally {
      setChartsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchCharts();  }, [fetchCharts]);

  // ── Refresh both at once ─────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    setSummaryLoading(true);
    setChartsLoading(true);
    await Promise.all([fetchSummary(true), fetchCharts(true)]);
    setRefreshing(false);
  };

  return (
    <Page title="Dashboard">
      <Container maxWidth="xl">

        {/* ── Welcome row with refresh icon ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 5 }}>
          <Typography variant="h3">Hi, Welcome back</Typography>
          <Tooltip title="Refresh dashboard">
            <IconButton
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                bgcolor: "#f2ebff",
                color: "#6a1b9a",
                "&:hover": { bgcolor: "#e9d8ff" },
                transition: "transform 0.3s",
                transform: refreshing ? "rotate(360deg)" : "none",
              }}
            >
              {refreshing
                ? <CircularProgress size={20} sx={{ color: "#6a1b9a" }} />
                : <Iconify icon="eva:refresh-fill" width={22} height={22} />
              }
            </IconButton>
          </Tooltip>
        </Box>

        {role === "Admin" && (
          <Grid container spacing={3}>

            {/* ── Summary widgets — shimmer while loading ──────────────── */}
            {[
              { title: "All Courses",     key: "course",         color: "primary",   icon: "eva:shopping-bag-fill" },
              { title: "All Users",       key: "user",           color: "info",      icon: "eva:people-fill" },
              { title: "All Teachers",    key: "teacher",        color: "warning",   icon: "fa-solid:chalkboard-teacher" },
              { title: "All Test Series", key: "testseries",     color: "error",     icon: "eva:file-text-fill" },
              { title: "Total Purchases", key: "purchase_count", color: "secondary", icon: "eva:shopping-cart-fill" },
            ].map((w, i) => (
              <Grid item xs={12} sm={6} md={2.4} key={w.key}>
                {summaryLoading
                  ? <ShimmerCard idx={i} />
                  : <AppWidgetSummary title={w.title} total={summary?.[w.key] || 0} color={w.color} icon={w.icon} />
                }
              </Grid>
            ))}

            {/* ── Charts — shimmer while loading ───────────────────────── */}
            {role !== "Teacher" && (
              <Grid item xs={12}>
                {chartsLoading
                  ? <ShimmerRect height={420} />
                  : (
                    <DashboardCharts
                      registrationTrend={dashboard?.registration_trend}
                      purchaseTrend={dashboard?.purchase_trend}
                      testAttemptTrend={dashboard?.test_attempt_trend}
                      allRegistrationTrend={dashboard?.all_registration_trend}
                      allPurchaseTrend={dashboard?.all_purchase_trend}
                      allTestAttemptTrend={dashboard?.all_test_attempt_trend}
                      todayRegistrationTrend={dashboard?.today_registration_trend}
                      todayPurchaseTrend={dashboard?.today_purchase_trend}
                      todayTestAttemptTrend={dashboard?.today_test_attempt_trend}
                    />
                  )
                }
              </Grid>
            )}

            {/* ── Recent tables — shimmer while loading ────────────────── */}
            {role !== "Teacher" && (
              <>
                <Grid item xs={12} md={6}>
                  {chartsLoading
                    ? <ShimmerRect height={300} />
                    : <RecentPurchases purchases={dashboard?.recent_purchases} />
                  }
                </Grid>
                <Grid item xs={12} md={6}>
                  {chartsLoading
                    ? <ShimmerRect height={300} />
                    : <NewlyResitered usersData={dashboard?.users} />
                  }
                </Grid>
              </>
            )}

          </Grid>
        )}
      </Container>
    </Page>
  );
}
